import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../../shared/components/ui/badge/badge.component';
import { AuditTimelineComponent } from '../../../../shared/components/audit/audit-timeline.component';
import { AuditApiService } from '../../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../../audit/models/audit.model';
import { WorkflowApiService } from '../services/workflow-api.service';
import { WorkflowMonitoringApiService } from '../services/workflow-monitoring-api.service';
import { WorkflowMonitoringSummaryComponent } from '../workflow-monitoring-summary/workflow-monitoring-summary.component';
import { WorkflowMonitoringDashboardResponse } from '../models/workflow-monitoring.model';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import {
  WorkflowResponse,
  WorkflowFolderResponse,
  WorkflowStatus,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_OBJECT_TYPE_LABELS
} from '../models/workflow.model';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';
type WorkflowTab = 'info' | 'monitoring' | 'timeline';

/**
 * Detalle estándar de Workflow — INC_CRM_020 (2) inc1 + inc4.
 *
 * <p>Sustituye a la antigua PÁGINA `/automation/workflows/:id`, que era el único detalle
 * del sistema que no seguía el patrón canónico (contactos, leads, segmentos, formularios y
 * business usan todos un modal con pestañas Información + Línea de tiempo) y que además
 * repetía en su encabezado los mismos 13 botones de la vista de lista.</p>
 *
 * <p>Aquí el pie solo ofrece "Editar" y "Cerrar", igual que el resto de detalles del
 * sistema: las acciones de estado (activar, pausar, duplicar, plantilla, archivar) viven en
 * la fila de la lista, que es su sitio, y no se duplican. Constructor, Inscripciones y
 * Pruebas y activación son configuración/operación y se abren desde el menú "⋯" de la fila.</p>
 *
 * <p>El MONITOREO sí vive aquí, en su propia pestaña. Al sustituir la página de detalle se
 * sacó junto con el resto de botones dando por hecho que "ya tiene su propio módulo", pero esa
 * página (`/automation/workflows/:id/monitoring`) se quedó sin ningún enlace en toda la
 * aplicación: los estados de ejecución y sus contadores dejaron de ser alcanzables. La pestaña
 * muestra el resumen (total / en ejecución / exitosos / fallidos…) y enlaza a la página
 * completa, que además trae la serie temporal y los registros inscritos ahora mismo.</p>
 */
@Component({
  selector: 'app-workflow-detail-modal',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule,
    ModalComponent, BadgeComponent, AuditTimelineComponent, WorkflowMonitoringSummaryComponent
  ],
  templateUrl: './workflow-detail-modal.component.html'
})
export class WorkflowDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  /**
   * Fila de la LISTA. `GET /api/workflows` devuelve `WorkflowListResponse`, un subconjunto:
   * no trae `settings`, `maxActiveEnrollments`, `reEnrollmentEnabled` ni las marcas de
   * activado/pausado/archivado. Se usa para pintar el modal al instante y se sustituye por el
   * detalle completo en cuanto llega `GET /api/workflows/{id}` (ver `detalle`).
   */
  @Input() workflow: WorkflowResponse | null = null;
  /** Carpetas del negocio, para resolver el nombre a partir de `idFolder`. */
  @Input() folders: WorkflowFolderResponse[] = [];
  /** Nombre del responsable ya resuelto por el contenedor (la lista ya carga usuarios). */
  @Input() ownerName = '';

  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<WorkflowResponse>();

  activeTab: WorkflowTab = 'info';

  timelineLogs: AuditLogResponse[] = [];
  timelineLoading = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;
  /** Evita recargar el timeline en cada ida y vuelta al tab cuando no hay eventos. */
  timelineLoaded = false;

  /** INC_CRM_020 (2) inc6 — la descripción larga se recorta y se despliega bajo demanda. */
  descriptionExpanded = false;

  /**
   * INC_CRM_020 (4) inc4 — detalle COMPLETO traído por id.
   *
   * <p>El modal recibía la fila de la lista y pintaba con ella las secciones «Inscripción»
   * (re-inscripción, máximo de activas, criterios de salida, reglas de supresión) y las marcas
   * de activado/pausado/archivado. Ninguno de esos campos viaja en `WorkflowListResponse`, así
   * que el detalle mostraba siempre los valores por defecto — «Inscripción única», «Sin límite»,
   * «Sin criterios», «Sin reglas» — con independencia de cómo estuviera configurado el
   * workflow. Ahora se relee por id al abrir y todos los getters leen de aquí.</p>
   */
  detalle: WorkflowResponse | null = null;
  detalleLoading = false;

  /** Fuente de verdad para la plantilla: el detalle si ya llegó, si no la fila de la lista. */
  get vm(): WorkflowResponse | null {
    return this.detalle ?? this.workflow;
  }

  // ── Monitoreo ─────────────────────────────────────────────────────────────
  //
  // Estados de ejecución del workflow y sus contadores. Se pide bajo demanda al abrir la
  // pestaña: el dashboard recorre inscripciones, runs y steps, y no compensa pagarlo cada
  // vez que alguien mira la ficha.

  monitoring: WorkflowMonitoringDashboardResponse | null = null;
  monitoringLoading = false;
  monitoringError = '';
  monitoringLoaded = false;
  monitoringRangeDays: 30 | 60 | 90 = 30;
  /** Las ejecuciones de prueba se excluyen por defecto, como en la página de monitoreo. */
  monitoringIncludeTestRuns = false;

  readonly monitoringRanges: (30 | 60 | 90)[] = [30, 60, 90];

  readonly tabs: { id: WorkflowTab; label: string }[] = [
    { id: 'info', label: 'Información' },
    { id: 'monitoring', label: 'Monitoreo' },
    { id: 'timeline', label: 'Línea de tiempo' }
  ];

  constructor(
    private auditApi: AuditApiService,
    private datetimeService: DatetimeService,
    private workflowApi: WorkflowApiService,
    private monitoringApi: WorkflowMonitoringApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.activeTab = 'info';
      this.descriptionExpanded = false;
      this.timelineLogs = [];
      this.timelineError = '';
      this.timelinePage = 0;
      this.timelineTotalPages = 0;
      this.timelineTotalElements = 0;
      this.timelineLoaded = false;
      this.monitoring = null;
      this.monitoringError = '';
      this.monitoringLoaded = false;
      this.monitoringRangeDays = 30;
      this.monitoringIncludeTestRuns = false;
      this.cargarDetalle();
    }
  }

  /** Si falla, se conserva la fila de la lista: el modal degrada, no se rompe. */
  private cargarDetalle(): void {
    this.detalle = null;
    const id = this.workflow?.id;
    if (!id) return;
    this.detalleLoading = true;
    this.workflowApi.getById(id).subscribe({
      next: (full) => { this.detalle = full; this.detalleLoading = false; },
      error: () => { this.detalleLoading = false; }
    });
  }

  setTab(tab: WorkflowTab): void {
    this.activeTab = tab;
    if (tab === 'timeline' && !this.timelineLoaded && !this.timelineLoading && this.workflow) {
      this.cargarTimeline(0);
    }
    if (tab === 'monitoring' && !this.monitoringLoaded && !this.monitoringLoading && this.workflow) {
      this.cargarMonitoreo();
    }
  }

  /** Recarga al cambiar el rango o el interruptor de pruebas, y con el botón "Actualizar". */
  cargarMonitoreo(): void {
    const id = this.workflow?.id;
    if (!id) return;
    this.monitoringLoading = true;
    this.monitoringError = '';
    this.monitoringApi.getMonitoringDashboard(id, {
      rangeDays: this.monitoringRangeDays,
      includeTestRuns: this.monitoringIncludeTestRuns
    }).subscribe({
      next: (res) => {
        this.monitoring = res;
        this.monitoringLoading = false;
        this.monitoringLoaded = true;
      },
      error: (err) => {
        this.monitoring = null;
        this.monitoringError = err?.status === 403
          ? 'No tienes permiso para ver el monitoreo de workflows.'
          : (err?.error?.message ?? 'No se pudo cargar el monitoreo.');
        this.monitoringLoading = false;
        // No se marca como cargado: al volver a la pestaña se reintenta.
      }
    });
  }

  cambiarRangoMonitoreo(dias: 30 | 60 | 90): void {
    if (this.monitoringRangeDays === dias) return;
    this.monitoringRangeDays = dias;
    this.cargarMonitoreo();
  }

  /**
   * INC_CRM_020 (2) inc7 — el nombre de entidad va en minúscula ('workflow'), como el resto
   * del sistema: la comparación contra `audit_log.entity_name` distingue mayúsculas.
   */
  cargarTimeline(page: number): void {
    if (!this.workflow?.id) return;
    this.timelineLoading = true;
    this.timelineError = '';
    this.timelinePage = page;
    this.auditApi.getEntityTimeline('workflow', String(this.workflow.id), { page, size: 15 }).subscribe({
      next: (res) => {
        this.timelineLogs = res.content ?? [];
        this.timelinePage = res.page ?? page;
        this.timelineTotalPages = res.totalPages ?? 0;
        this.timelineTotalElements = res.totalElements ?? 0;
        this.timelineLoading = false;
        this.timelineLoaded = true;
      },
      error: (err) => {
        this.timelineLogs = [];
        this.timelineError = err?.status === 403
          ? 'No tienes permiso para ver el historial de auditoría.'
          : 'Error al cargar la línea de tiempo.';
        this.timelineLoading = false;
        // No se marca como cargada: al volver al tab se reintenta.
      }
    });
  }

  // ── Presentación ──────────────────────────────────────────────────────────

  get statusLabel(): string {
    const s = this.vm?.status;
    return s ? (WORKFLOW_STATUS_LABELS[s] ?? s) : '';
  }

  get statusColor(): BadgeColor {
    switch (this.vm?.status as WorkflowStatus) {
      case 'ACTIVE':   return 'success';
      case 'PAUSED':   return 'warning';
      case 'ARCHIVED': return 'error';
      default:         return 'light';
    }
  }

  get objectTypeLabel(): string {
    const t = this.vm?.objectType;
    return t ? (WORKFLOW_OBJECT_TYPE_LABELS[t] ?? t) : '—';
  }

  get folderName(): string {
    const id = this.vm?.idFolder;
    if (!id) return '—';
    return this.folders.find(f => f.id === id)?.name ?? '—';
  }

  get initial(): string {
    return (this.vm?.name || '?').charAt(0).toUpperCase();
  }

  get isDescriptionLong(): boolean {
    const texto = this.vm?.description ?? '';
    return texto.length > 200 || (texto.match(/\n/g)?.length ?? 0) > 2;
  }

  /**
   * Etiquetas normalizadas. `WorkflowResponse` declara `tags: string[]` como obligatorio,
   * pero la respuesta del LISTADO no siempre lo trae: acceder directo a `workflow.tags.length`
   * en la plantilla lanzaba "Cannot read properties of undefined" durante el change detection
   * y el modal no llegaba a pintarse (fallo silencioso: el clic no hacía nada).
   *
   * Ojo con el aviso NG8107 del compilador ("puedes quitar el ?."): se basa en ese tipo que
   * miente, así que aquí la normalización vive en el componente y la plantilla queda limpia.
   */
  get tags(): string[] {
    return this.vm?.tags ?? [];
  }

  get priorityOrder(): number {
    return this.vm?.priorityOrder ?? 0;
  }

  /** Ajustes guardados en `settings` (criterios de salida, supresión, re-inscripción…). */
  get settings(): Record<string, any> {
    return this.vm?.settings ?? {};
  }

  get exitCriteriaCount(): number {
    return (this.settings['exitCriteria'] ?? []).length;
  }

  get suppressionRulesCount(): number {
    return (this.settings['suppressionRules'] ?? []).length;
  }

  get allowsReEnrollment(): boolean {
    return !!(this.settings['allowReEnrollment'] || this.vm?.reEnrollmentEnabled);
  }

  formatDate(value?: string | null): string {
    return value ? this.datetimeService.formatDateTime(value) : '—';
  }

  onClose(): void { this.close.emit(); }

  onEdit(): void {
    if (this.workflow) this.edit.emit(this.workflow);
  }
}
